import React, { useContext } from 'react'
import { CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { LeadDetailsDto, LeadIdSourceEnum } from 'api/Leads/Types/LeadDetailsDto'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { hasPermission } from 'utils/permissionsValidator'
import { TableWithExport } from 'utils/table/withExport'

interface Props {
  data: LeadDetailsDto
  onResetActivity(): void
}

export const LeadActivitiesTable: React.FC<Props> = ({ data, onResetActivity }) => {
  const { activities } = data

  const [auth] = useContext(AuthSessionContext)

  const permissions = auth?.permissions || []

  const isMobileRegistrationOrLiveRegistration =
    data.activities.find((x) => x.leadSource.id === LeadIdSourceEnum.IsLiveRegistration) ||
    data.activities.find((x) => x.leadSource.id === LeadIdSourceEnum.IsMobileRegistration)

  const products = data.products

  const isResetActivityAllowed = isMobileRegistrationOrLiveRegistration
    ? data.isActivityReset
    : !data.isActivityReset

  return (
    <TableWithExport
      extraActions={
        hasPermission(permissions, PermissionsEnum.CanResetActivity) && (
          <Button
            variant='destructive'
            size='sm'
            onClick={onResetActivity}
            disabled={isResetActivityAllowed || data.isConverted}
          >
            Reset Activity
          </Button>
        )
      }
      data={activities}
    >
      <Grid data={activities} total={activities.length} resizable style={{ zIndex: 0 }}>
        <Column
          minResizableWidth={60}
          width={60}
          title='Title'
          field='title'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Firstname'
          field='firstName'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Lastname'
          field='lastName'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Phone'
          field='phoneMobile'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Lead Source'
          field='leadSource.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Lead Source Desc.'
          field='leadSourceDescription'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Product'
          cell={(gridCellProps: GridCellProps) => {
            const activityProduct = gridCellProps.dataItem.products.map((product: NameDto) => (
              <p>{product.name}</p>
            ))
            if (!products.length && !gridCellProps.dataItem?.products.length) return <td />
            return <td>{activityProduct ?? products.map((product) => <p>{product.name}</p>)}</td>
          }}
          className='table-columns__content'
        />
        <Column
          minResizableWidth={80}
          width={80}
          title='Country'
          field='country.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={80}
          width={80}
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem?.tickmillCompany?.id) {
              return <td />
            }

            return (
              <td>
                <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                  {gridCellProps.dataItem.tickmillCompany.name}
                </CompanyTag>
              </td>
            )
          }}
          title='Entity'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={80}
          width={80}
          title='Opt In'
          cell={(gridCellProps) => (
            <td className='table-columns__date'>
              <input type='checkbox' checked={gridCellProps.dataItem.optIn} readOnly />
            </td>
          )}
          className='table-columns__content'
        />
        <Column
          title='Campaign'
          children={[
            <Column
              minResizableWidth={120}
              width={120}
              title='Name'
              cell={(gridCellProps) => {
                const isLinkCampaign = gridCellProps.dataItem.campaign === 'ib_link'
                if (isLinkCampaign) {
                  return (
                    <td className={'table-columns__content'}>
                      {gridCellProps.dataItem.campaignMedium}
                    </td>
                  )
                }
                return (
                  <td className={'table-columns__content'}>{gridCellProps.dataItem.campaign}</td>
                )
              }}
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Medium'
              cell={(gridCellProps) => {
                const isLinkCampaign = gridCellProps.dataItem.campaign === 'ib_link'
                if (isLinkCampaign) {
                  return (
                    <td className={'table-columns__content'}>
                      {gridCellProps.dataItem.campaignMedium ? 'ib' : null}
                    </td>
                  )
                }
                return (
                  <td className={'table-columns__date'}>{gridCellProps.dataItem.campaignMedium}</td>
                )
              }}
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Platform'
              field='campaignTerm'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Content'
              field='campaignContent'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Source'
              field='campaign'
              cell={(gridCellProps) => {
                const isLinkCampaign = gridCellProps.dataItem.campaign === 'ib_link'
                if (isLinkCampaign) {
                  return (
                    <td className={'table-columns__content'}>{gridCellProps.dataItem.campaign}</td>
                  )
                }
                return (
                  <td className={'table-columns__content'}>
                    {gridCellProps.dataItem.campaignSource}
                  </td>
                )
              }}
              className='table-columns__content'
            />,
          ]}
          className='table-columns__content'
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Referrer'
          field='referrer'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Referral Code'
          field='referralCode'
          className='table-columns__content'
        />
        {activities.some((activity) => !!activity.sglAgent) && (
          <Column
            minResizableWidth={120}
            width={120}
            title='SGL Agent'
            field='sglAgent.name'
            className='table-columns__content'
          />
        )}
        <Column
          minResizableWidth={120}
          width={120}
          title='Affiliate Token'
          field='affiliateToken'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Trading Account'
          children={[
            <Column
              minResizableWidth={120}
              width={120}
              title='Login'
              field='tradingAccountLogin'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Group'
              field='tradingAccountGroup'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Server'
              field='tradingAccountServer'
              className='table-columns__content'
            />,
          ]}
          className='table-columns__content'
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Created'
          cell={(gridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.created, true)}</td>
          )}
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Updated'
          cell={(gridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.updated, true)}</td>
          )}
        />
      </Grid>
    </TableWithExport>
  )
}
